import { useState, type ChangeEvent, type Ref } from "react";
import { useTranslation } from "react-i18next";

import type { Profile } from "@/api/bale.zod";

import {
  useAddFriendGroupMember,
  useGetFriendGroupSuspense,
  useListFriends,
  useRemoveFriendGroupMember,
} from "@/api/bale";
import { ActionBox } from "@/components/ActionBox";
import { CardActionsHeader } from "@/components/Card";
import { Dialog } from "@/components/Dialog";
import { Icon } from "@/components/Icon";
import { IconButton } from "@/components/IconButton";
import { Input } from "@/components/Input";
import { ProfileItem } from "@/components/ProfileItem";

export function AddFriendGroupMemberModal({
  ref,
  friendGroupId,
  onClose,
}: {
  ref: Ref<HTMLDialogElement>;
  friendGroupId: number;
  onClose?: () => void;
}) {
  const { t } = useTranslation();
  const listFriends = useListFriends("@me", { query: { select: (data) => data.data.friends } });
  const [searchValue, setSearchValue] = useState("");
  const onSearch = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.currentTarget.value);
  };

  const matching = listFriends.data?.filter((friend) => friend.profile.id.startsWith(searchValue)) ?? [];

  return (
    <Dialog ref={ref} onClose={onClose} closeOnOutside className="flex h-[95vh] max-h-200 w-lg flex-col">
      <CardActionsHeader title={t("adjust-members")}>
        <Input type="search" name="id" onChange={onSearch} placeholder={t("search-by-name")} />
      </CardActionsHeader>
      <div className="flex min-h-0 grow flex-col overflow-y-auto">
        {listFriends.isFetching && (
          <div className="flex grow items-center justify-center p-8 font-interest text-placeholder">
            <Icon name="loader" className="animate-spin" />
          </div>
        )}
        {listFriends.isSuccess && listFriends.data.length === 0 && (
          <div className="flex grow items-center justify-center p-8 font-interest text-placeholder">
            {t("no-friends-found")}
          </div>
        )}
        {matching?.map((friend) => (
          <FriendItem
            key={friend.profile.id}
            friendGroupId={friendGroupId}
            profile={friend.profile}
            note={friend.note}
          />
        ))}
      </div>
    </Dialog>
  );
}

function FriendItem({
  profile,
  note,
  friendGroupId,
}: {
  profile: Profile;
  note?: string | null;
  friendGroupId: number;
}) {
  const isMember = useGetFriendGroupSuspense(friendGroupId, {
    query: { select: (data) => data.data.members.some((member) => member.profile.id === profile.id) },
  });
  const addFriendGroupMember = useAddFriendGroupMember();
  const removeFriendGroupMember = useRemoveFriendGroupMember();

  function toggleMembership() {
    if (isMember.data) {
      removeFriendGroupMember.mutate({ friendGroupId, data: { friend_id: profile.id } });
    } else {
      addFriendGroupMember.mutate({ friendGroupId, data: { friend_id: profile.id } });
    }
  }

  return (
    <div className="relative flex flex-row justify-between gap-4 p-2 pl-4">
      <ProfileItem note={note} profile={profile} />
      <ActionBox>
        <IconButton
          type="button"
          className={isMember.data ? "text-danger" : undefined}
          name={isMember.data ? "user-minus" : "user-plus"}
          onClick={toggleMembership}
          disabled={isMember.isLoading}
        />
      </ActionBox>
    </div>
  );
}
